﻿import $ from 'jquery';
import Element from "./element";

declare global {

  interface JQuery {
    valuebinding(options?: IValueBindingOptions);
  }
}

export class ValueBinding {
  public static get wireup(): string {
    return this.wireupName.asDataSelector();
  }

  private static wireupName: string = 'value-bind';
  private static suppressedKey = '_suppressed';
  private element: Element;
  private getValue: (element: Element) => any;
  private setValue: (element: Element, value: any) => void;
  private hasSuppressedBindTo: boolean;

  private DEFAULTS: IValueBindingOptions = {
    bindOn: 'input',
    bindFrom: 'value',
    bindTo: 'value',
    bindBehavior: 'set',
    key: null,
    nullToken: null
  };

  private bindToMap: any[] = [
    { key: 'text', binder: (e, v) => e.text(v) },
    { key: 'value', binder: (e, v) => e.value(v) },
    { key: `value${ValueBinding.suppressedKey}`, binder: (e, v) => e.suppressValueChange(v) }
  ];

  private bindFromMap: any[] = [
    { key: 'text', binder: e => e.text() },
    { key: 'value', binder: e => e.value() }
  ];

  constructor(private $element: JQuery, private options: IValueBindingOptions) {
    if (!this.$element || this.$element.length == 0)
      throw Error('You must provide an element to Rustig.ValueBinding');

    this.options = $.extend({}, this.DEFAULTS, this.options, this.$element.data())
    this.options.key = this.options.key || this.$element.data(ValueBinding.wireupName);
    this.element = new Element(this.$element);

    if (!this.options.key)
      throw Error('You must provide a key to Rustig.ValueBinding');

    this.setGetValue();
    this.setSetValue();

    this.options.bindOn.split(',').forEach((event) => {
      this.bindEvent(event.trim());
    });
  }

  private bindEvent(event: string) {
    let key = 'rustig.valuebinding.' + event + '.target';
    let eventName = event + '.rustig.valuebinding';
    let targets = <Array<Element>>this.$element.data(key);
    if (!targets) {
      let selector = '[data-value-bind-for="' + this.options.key + '"]';
      targets = Element.fromElements($(selector));
      this.$element.data(key, targets);
    }

    this.$element.on(eventName, () => {
      let value;
      let isFalseBool = this.options.bindWith !== undefined && !this.options.bindWith;

      if (isFalseBool)
        value = false;
      else
        value = this.options.bindWith || this.getValue(this.element);

      if (value === this.options.nullToken)
        value = null;

      targets.forEach((target: Element) => {
        let currentValue = this.getValue(target);

        if (this.options.bindBehavior === 'toggle' && currentValue === value) {
          value = null;
        }

        this.setValue(target, value);

        if (!target.isCheckbox && !this.hasSuppressedBindTo)
          target.$element.trigger(jQuery.Event(eventName));

        target.$element.trigger(jQuery.Event('rustig.valuebinding.valueset'));
      });
    });
  };

  private setGetValue(): void {
    for (let i = 0; i < this.bindFromMap.length; i++) {
      if (this.options.bindFrom === this.bindFromMap[i].key) {
        this.getValue = this.bindFromMap[i].binder;
        return;
      }
    }
    this.getValue = e => e.value();
  }

  private setSetValue(): void {
    this.hasSuppressedBindTo = this.options.bindTo.endsWith(ValueBinding.suppressedKey);

    for (let i = 0; i < this.bindToMap.length; i++) {
      if (this.options.bindTo === this.bindToMap[i].key) {
        this.setValue = this.bindToMap[i].binder;
        return;
      }
    }
    this.setValue = (e, v) => e.value(v);
  }
}

export interface IValueBindingOptions {
  key: string;
  bindOn: string;
  bindFrom?: string;
  bindTo?: string;
  bindWith?: string;
  bindBehavior?: string;
  nullToken?: string;
};

function Plugin(option) {
  return this.each(function () {
    let $this = $(this);
    let data = $this.data('rustig.valuebinding');
    let options = typeof option == 'object' && option;

    if (!data) {
      data = new ValueBinding($this, options);
      $this.data('rustig.valuebinding', data);
    }
  });
}

$.fn.valuebinding = Plugin;

